import { useCallback, useEffect } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Icon, Text, Fill, Stroke } from 'ol/style';
import { fetchLifestyleInfo } from 'services/lab2/lifestyleService';
import { Cluster } from 'ol/source';
import CircleStyle from 'ol/style/Circle';

const useLifestylePoints = ({
    mapRef,
    type,
    params,
    isMapReady,
    syncLayers,
    getPointSVG,
    selectedLocation,
    setSelectedLocation
}) => {
    const fetchPointLayer = useCallback(async () => {
        if (
            !isMapReady ||
            !mapRef.current ||
            !params?.extents ||
            !params?.layerName ||
            !type
        ) {
            return;
        }

        const apiName = type === 'hos' ? 'getHosInfo' : 'getEduInfo';
        const geojsonData = await fetchLifestyleInfo({
            endpoint: apiName,
            extent: params.extents,
            layerName: params.layerName,
        });

        // 시도, 시군구, 읍면동 
        if(params.layerName !== 'point') {
            syncLayers(
                geojsonData?.features?.length
                    ? [
                          {
                              id: 'lifestyle-point-layer',
                              createLayer: (mapInstance) => {
                                  const features = new GeoJSON().readFeatures(geojsonData, {
                                      featureProjection: mapInstance.getView().getProjection(),
                                  });
                                  if (!features.length) return null;
    
                                  const source = new VectorSource({ features });
                                  const iconCache = new Map();
                                  const styleCache = new Map();
    
                                  const resolveFeatureKey = (feature) => {
                                      const code =
                                          feature.get('admin_cd') ||
                                          feature.get('gid') ||
                                          feature.get('props')?.[0]?.adm_cd ||
                                          feature.getId() ||
                                          feature.ol_uid;
                                      const count =
                                          feature.get('count') ??
                                          feature.get('t_cnt') ??
                                          feature.get('props')?.length ??
                                          0;
                                      return `${type}-${params.layerName}-${code}-${count}`;
                                  };

                                return new VectorLayer({
                                    source,
                                    style: (feature) => {
                                        const key = resolveFeatureKey(feature);
                                        if (styleCache.has(key)) {
                                            return styleCache.get(key);
                                        }
  
                                        let iconSrc = iconCache.get(key);
                                        if (!iconSrc) {
                                            iconSrc =
                                                'data:image/svg+xml;utf8,' +
                                                encodeURIComponent(getPointSVG(feature));
                                            iconCache.set(key, iconSrc);
                                        }
  
                                        const style = new Style({
                                            image: new Icon({
                                                src: iconSrc,
                                                anchor: [0.5, 0.5],
                                                scale: 1,
                                            }),
                                            text: new Text({
                                                text: feature.get('admin_nm'),
                                                font: '12px SCDream4',
                                                fill: new Fill({ color: '#333' }),
                                                stroke: new Stroke({ color: '#fff', width: 3 }),
                                                offsetY: -25,
                                            }),
                                            zIndex: 1000,
                                        });
  
                                        styleCache.set(key, style);
                                        return style;
                                    },
                                });
                              },
                          },
                      ]
                    : []
            );
        }
        // Point 단위
        else if (params.layerName === 'point') {
            if(selectedLocation?.mode !== 'radius') {
                setSelectedLocation(undefined);
            }

            // Cluster 레이어
            syncLayers(
                geojsonData?.features?.length
                    ? [
                          {
                              id: 'lifestyle-point-layer',
                              createLayer: (mapInstance) => {
                                  const features = new GeoJSON().readFeatures(geojsonData, {
                                      featureProjection: mapInstance.getView().getProjection(),
                                  });

                                  if (!features.length) return null;
    
                                  const source = new VectorSource({ features });
                                  const styleCache = new Map();
    
                                  const resolveFeatureKey = (feature) => {
                                      const code =
                                          feature.get('admin_cd') ||
                                          feature.get('gid') ||
                                          feature.get('props')?.[0]?.adm_cd ||
                                          feature.getId() ||
                                          feature.ol_uid;
                                      const count =
                                          feature.get('count') ??
                                          feature.get('t_cnt') ??
                                          feature.get('props')?.length ??
                                          0;
                                      return `${type}-${params.layerName}-${code}-${count}`;
                                  };
    
                                  // Cluster 소스 생성
                                  const clusterSource = new Cluster({
                                      distance: 190 * Math.exp(-0.25 * (params.zoom - 15)) + 10,
                                      minDistance: 40,
                                      source: source
                                  });
  
                                  const clusterLayer = new VectorLayer({
                                      source: clusterSource,
                                      style: (feature) => {
                                          const key = resolveFeatureKey(feature);
                                            if (styleCache.has(key)) {
                                                return styleCache.get(key);
                                            }
  
                                            const size = feature.get("features").length;
          
                                            const style = new Style({
                                              image: new CircleStyle({
                                                  radius: size/4+30,
                                                  stroke: new Stroke({
                                                      color: "#fff"
                                                  }),
                                                  fill: new Fill({
                                                      color: "#3399CC"
                                                  })
                                              }),
                                              text: new Text({
                                                  text: size.toString(),
                                                  font: "24px SCDream",
                                                  fill: new Fill({
                                                      color: "#fff"
                                                  })
                                              }),
                                              zIndex: 1000,
                                            })

                                            feature.set('admin_nm', '상세');

                                            const pointFeatures = feature.get("features");

                                            let typeSummary = {}

                                            if(type === 'hos') {
                                                typeSummary = {
                                                    h_cnt_01_11: 0,
                                                    h_cnt_21_31: 0,
                                                    h_cnt_28: 0,
                                                    h_cnt_41_51: 0,
                                                    h_cnt_61: 0,
                                                    h_cnt_71_72_73_75: 0,
                                                    h_cnt_81: 0,
                                                    h_cnt_92_93: 0,
                                                };

                                                pointFeatures.forEach((pointFeature) => {
                                                    const raw_type_cd = pointFeature.get('raw_type_cd');

                                                    if(raw_type_cd === '01' || raw_type_cd === '11') {
                                                        typeSummary.h_cnt_01_11 += 1
                                                    } else if(raw_type_cd === '21' || raw_type_cd === '31') {
                                                        typeSummary.h_cnt_21_31 += 1
                                                    } else if(raw_type_cd === '28') {
                                                        typeSummary.h_cnt_28 += 1
                                                    } else if(raw_type_cd === '41' || raw_type_cd === '51') {
                                                        typeSummary.h_cnt_41_51 += 1
                                                    } else if(raw_type_cd === '61') {
                                                        typeSummary.h_cnt_61 += 1
                                                    } else if(raw_type_cd === '71' || raw_type_cd === '72' || raw_type_cd === '73' || raw_type_cd === '75') {
                                                        typeSummary.h_cnt_71_72_73_75 += 1
                                                    } else if(raw_type_cd === '81') {
                                                        typeSummary.h_cnt_81 += 1
                                                    } else if(raw_type_cd === '92' || raw_type_cd === '93') {
                                                        typeSummary.h_cnt_92_93 += 1
                                                    }
                                                });
                                            } else if(type === 'edu') {
                                                typeSummary = {
                                                    e_cnt_01: 0,
                                                    e_cnt_02: 0,
                                                    e_cnt_03: 0,
                                                    e_cnt_04_05_06: 0,
                                                    e_cnt_06: 0,
                                                    e_cnt_07: 0,
                                                    e_cnt_08: 0,
                                                    e_cnt_09: 0,
                                                    e_cnt_10: 0,
                                                };

                                                pointFeatures.forEach((pointFeature) => {
                                                    const raw_type_cd = pointFeature.get('raw_type_cd');

                                                    if(raw_type_cd === '01') {
                                                        typeSummary.e_cnt_01 += 1
                                                    } else if(raw_type_cd === '02') {
                                                        typeSummary.e_cnt_02 += 1
                                                    } else if(raw_type_cd === '03') {
                                                        typeSummary.e_cnt_03 += 1
                                                    } else if(raw_type_cd === '04' || raw_type_cd === '05' || raw_type_cd === '06') {
                                                        typeSummary.e_cnt_04_05_06 += 1
                                                    } else if(raw_type_cd === '07') {
                                                        typeSummary.e_cnt_07 += 1
                                                    } else if(raw_type_cd === '08') {
                                                        typeSummary.e_cnt_08 += 1
                                                    } else if(raw_type_cd === '09') {
                                                        typeSummary.e_cnt_09 += 1
                                                    } else if(raw_type_cd === '10') {
                                                        typeSummary.e_cnt_10 += 1
                                                    }
                                                });
                                            }

                                            feature.setProperties(typeSummary);
      
                                            styleCache.set(key, style);
                                            return style;
                                      }
                                  });
  
                                  return clusterLayer;
                              },
                          },
                      ]
                    : []
            );
        }

    }, [getPointSVG, isMapReady, mapRef, params, syncLayers, type]);

    useEffect(() => {
        fetchPointLayer();
    }, [fetchPointLayer]);

    return { refreshPoints: fetchPointLayer };
};

export default useLifestylePoints;
